import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react'
import type { FieldProps } from 'formik'
import { ErrorMessage, Field } from 'formik'
import type { FocusEvent } from 'react'
import { Fragment, useEffect, useState } from 'react'

import ArrowDown from '@Icons/icon-wrap-down.svg?react'

export type DropdownOption = {
  label: string
  value: string
  disabled?: boolean
  id?: number
}

type Props = {
  options: DropdownOption[]
  placeholder?: string
  className?: string
  labelClassName?: string
  onChange?: (opt: DropdownOption) => void
  onBlur?: (e: FocusEvent<HTMLDivElement, Element>) => void
  defaultSelectedIndex?: number
  name?: string
  hasError?: boolean
  value?: DropdownOption
  showWarning?: boolean
  showMessage?: boolean
  disabled?: boolean
  disableFormikUpdate?: boolean
  customHeight?: string
  dropdownPlacement?: 'top' | 'bottom' // to handle this: https://github.com/tailwindlabs/headlessui/issues/3476#issuecomment-2385581973
}

export default function Dropdown({
  name,
  onChange,
  showWarning,
  showMessage = true,
  disableFormikUpdate = false,
  ...props
}: Props) {
  if (name) {
    return (
      <Field name={name}>
        {({ field, form, meta }: FieldProps) => (
          <div>
            <DropdownElement
              {...props}
              {...field}
              hasError={meta.touched && !!meta.error}
              onBlur={(e) => field.onBlur(e)}
              onChange={(opt: any) => {
                onChange?.(opt)
                if (!disableFormikUpdate) form?.setFieldValue(field?.name, opt)
              }}
              value={meta.value}
              showWarning={showWarning}
            />
            {showMessage && (
              <div className="absolute">
                <ErrorMessage name={name}>
                  {(msg: any) => (
                    <>
                      <div className="text-2xs pt-2 pl-0.5 text-red-500">
                        {typeof msg === 'string' || msg instanceof String
                          ? msg
                          : msg['value']}
                      </div>
                    </>
                  )}
                </ErrorMessage>
              </div>
            )}
          </div>
        )}
      </Field>
    )
  }

  return <DropdownElement {...props} name={name} onChange={onChange} />
}

function DropdownElement({
  options = [],
  placeholder,
  className = '',
  labelClassName = 'block truncate',
  onChange,
  onBlur,
  defaultSelectedIndex = 0,
  hasError,
  value,
  showWarning,
  disabled = false,
  customHeight,
  dropdownPlacement = 'bottom',
}: Props) {
  const [selected, setSelected] = useState<DropdownOption | null>(null)

  useEffect(() => {
    if (value) setSelected(value)
    else {
      setSelected(options[defaultSelectedIndex])
      onChange?.(options[defaultSelectedIndex])
    }
  }, [value, defaultSelectedIndex])

  const onSelect = (opt: DropdownOption) => {
    setSelected(opt)
    onChange?.(opt)
  }

  return (
    <Listbox value={selected} onChange={onSelect} disabled={disabled}>
      <div className={`relative text-gray-700 ${className}`} onBlur={onBlur}>
        <ListboxButton
          style={{ boxShadow: showWarning ? '0 0 3px 1px red' : '' }}
          className={`relative w-full cursor-default rounded-lg bg-white px-3.5 text-left focus:outline-none border flex items-center justify-between gap-5 shadow-sm disabled:opacity-75 disabled:cursor-not-allowed disabled:bg-[rgba(239,239,239,0.3)] ${
            hasError ? 'border-red-300' : 'border-gray-300'
          }${customHeight ? ' ' + customHeight : ' py-2'} `}
        >
          <span className={labelClassName}>
            {selected ? (
              selected.label
            ) : (
              <span className="text-gray-500">
                {placeholder || 'Select...'}
              </span>
            )}
          </span>
          <span className="pointer-events-none">
            <ArrowDown />
          </span>
        </ListboxButton>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ListboxOptions
            className={`absolute ${dropdownPlacement === 'top' ? 'bottom-full' : 'top-full'} z-10 my-1 max-h-60 w-min min-w-full overflow-auto rounded-lg py-1 bg-white text-base focus:outline-none sm:text-sm border shadow-sm`}
            modal={false}
          >
            {options.map((option, i) => (
              <ListboxOption
                key={i}
                className={({ focus }) =>
                  `relative cursor-default select-none py-2 px-4 ${
                    focus ? 'bg-gray-50' : ''
                  }${option?.disabled ? ' opacity-75 cursor-not-allowed' : ''}`
                }
                value={option}
                disabled={option?.disabled}
              >
                {({ selected }) => (
                  <span className={`block truncate ${selected ? '' : ''}`}>
                    {option.label}
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  )
}
